import React, { useState } from 'react'
import { Languages, Loader2, AlertTriangle, Copy, Check } from 'lucide-react'
import { useTranslate } from '../hooks/useResearch'
import Markdown from './Markdown'

export const TARGET_LANGS = [
  ['en', 'English'], ['cs', 'Czech'], ['de', 'German'], ['fr', 'French'],
  ['es', 'Spanish'], ['it', 'Italian'], ['pl', 'Polish'], ['pt', 'Portuguese'],
  ['ru', 'Russian'], ['uk', 'Ukrainian'], ['nl', 'Dutch'],
]

const selCls =
  'h-8 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist'

// "Translate this output" — translate an answer/snippet to a chosen language and
// show it inline, rendered as Markdown like the answer. Source language is
// auto-detected by the BFF; when `project` is set the result is saved to that
// project's translation history.
export default function TranslateButton({ text, defaultTarget = 'en', project }) {
  const translate = useTranslate()
  const [target, setTarget] = useState(defaultTarget)
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const result = translate.data?.translated

  const onTranslate = () => {
    if (!text || !text.trim()) return
    setOpen(true)
    setCopied(false)
    translate.mutate({ text, target_lang: target, project, save: !!project })
  }
  const copy = () => {
    navigator.clipboard?.writeText(result || '').then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <select value={target} onChange={(e) => setTarget(e.target.value)} className={selCls} aria-label="Target language">
          {TARGET_LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
        </select>
        <button
          onClick={onTranslate}
          disabled={translate.isPending || !text}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-medium border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-50 dark:text-gold transition"
        >
          {translate.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}
          {translate.isPending ? 'Translating…' : 'Translate'}
        </button>
      </div>

      {open && translate.isError && (
        <p className="inline-flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="w-3.5 h-3.5" /> {translate.error?.message}</p>
      )}
      {open && result && (
        <div className="relative rounded-lg border border-parchment-border bg-white/60 p-3 pr-9 dark:border-midnight-600 dark:bg-midnight-800/50">
          <Markdown>{result}</Markdown>
          {project && <p className="mt-1 text-[11px] text-ink-soft/70 dark:text-mist/40">Saved to {project}’s translations.</p>}
          <button onClick={copy} className="absolute top-2 right-2 p-1 rounded text-ink-soft hover:text-ink dark:text-mist/50" aria-label="Copy">
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
      {open && !translate.isPending && !translate.isError && translate.data && !result && (
        <p className="text-xs text-ink-soft dark:text-mist/50 italic">No translation returned (check the T8R backend).</p>
      )}
    </div>
  )
}
